import { useState, useEffect, useContext } from 'react';
import { Grid, Box, Alert } from '@mui/material';
import RedoIcon from '@mui/icons-material/Redo';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttDialog,
    SttFormText,
    SttFormTextarea,
    SttLabelDate,
} from '../../../sporttia/all';
import { subsetObject } from '../../../lib/utils';

type RemittenceLine = {
    id: number;
    name: string;
    description: string;
    iban: string;
    rejected?: string;
};

type Props = {
    remittenceLine?: RemittenceLine | null;
    onClose: () => void;
    onUpdate?: (remittenceLine: RemittenceLine) => void;
};

/**
 * Remittence line
 * ===============
 *
 * Props
 *
 * 	* object remittenceLine
 * 	* function onClose
 * 	* function onUpdate(remittenceLine): call it when the internal line is updated
 */
export default function RemittenceLineDialog({
    remittenceLine = null,
    onClose,
    onUpdate,
}: Props): React.ReactElement | null {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [internalLine, setInternalLine] = useState<RemittenceLine>();

    /**
     * Remittence line changes
     */
    useEffect(() => {
        setInternalLine(remittenceLine || undefined);
    }, [remittenceLine]);

    /**
     * Handle form
     */
    const handleForm = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }): void => {
        if (!name) return;
        setInternalLine({ ...internalLine!, [name]: value });
    };

    /**
     * Save line
     */
    const saveLine = (): void => {
        cxt?.api('PUT', `/remittences/lines/${internalLine?.id}`, {
            params: subsetObject(internalLine!, [
                'name',
                'description',
                'iban',
            ]),
            success: (r: { remittenceLine: RemittenceLine }) => {
                setInternalLine(r.remittenceLine);
                cxt?.showMessage('S', t('Saved'));
                if (onUpdate) onUpdate(r.remittenceLine);
            },
        });
    };

    /**
     * Delete line
     */
    const deleteLine = (): void => {
        cxt?.api('DELETE', `/remittences/lines/${internalLine?.id}`, {
            confirmation: true,
            success: (r: { remittenceLine: RemittenceLine }) => {
                setInternalLine(r.remittenceLine);
                cxt?.showMessage('S', t('Deleted'));
                if (onUpdate) onUpdate(r.remittenceLine);
            },
        });
    };

    /**
     * Return line
     */
    const returnLine = (): void => {
        cxt?.api('PUT', `/remittences/lines/${internalLine?.id}/reject`, {
            confirmation: true,
            success: (r: { remittenceLine: RemittenceLine }) => {
                setInternalLine(r.remittenceLine);
                cxt?.showMessage('S', t('Returned'));
                if (onUpdate) onUpdate(r.remittenceLine);
            },
        });
    };

    // -----| Render |-----
    return (
        <SttDialog
            title={t('Line')}
            open={remittenceLine !== null}
            onClose={onClose}
            content={
                <Grid container spacing={3}>
                    {internalLine && (
                        <>
                            {internalLine.rejected && (
                                <Grid size={{ md: 12 }}>
                                    <Box mb={2}>
                                        <Alert
                                            variant="filled"
                                            severity="warning"
                                        >
                                            {t('Returned')}:{' '}
                                            <SttLabelDate
                                                date={internalLine.rejected}
                                            />
                                        </Alert>
                                    </Box>
                                </Grid>
                            )}

                            <SttFormText
                                grid
                                md={12}
                                name="name"
                                caption={t('Name')}
                                defVal={internalLine.name}
                                onChange={handleForm}
                            />

                            <SttFormTextarea
                                grid
                                name="description"
                                caption={t('Description')}
                                defVal={internalLine.description}
                                onChange={handleForm}
                            />

                            <SttFormText
                                grid
                                md={12}
                                name="iban"
                                caption={t('IBAN')}
                                defVal={internalLine.iban}
                                onChange={handleForm}
                            />
                        </>
                    )}
                </Grid>
            }
            buttons={[
                {
                    show: internalLine && !internalLine.rejected,
                    caption: t('Return'),
                    icon: <RedoIcon />,
                    onClick: () => returnLine(),
                },
                {
                    show: internalLine && !internalLine.rejected,
                    type: 'delete',
                    onClick: () => deleteLine(),
                },
                {
                    show: internalLine && !internalLine.rejected,
                    type: 'save',
                    onClick: () => saveLine(),
                },
            ]}
        />
    );
}
